import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Paperclip,
  Smile,
  Reply,
  Edit,
  Trash2,
  MoreVertical,
  User as UserIcon,
  X,
  Minimize2,
  Maximize2,
  Image,
  File
} from 'lucide-react';
import type { ChatMessage, User, Issue } from '../../types';
import { FilePreviewModal, FilePreviewItem } from '../UI/FilePreviewModal';
import { apiService } from '../../services/api';
import { UserAvatar } from '../UI/UserAvatar';

interface IssueChatProps {
  issue: Issue;
  messages: ChatMessage[];
  users: User[];
  currentUser: User;
  onSendMessage: (content: string, mentions?: string[], fileUrl?: string, fileName?: string, fileSize?: number, messageType?: string) => void;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  onClose?: () => void;
}

export const IssueChat: React.FC<IssueChatProps> = ({
  issue,
  messages,
  users,
  currentUser,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  isMinimized = false,
  onToggleMinimize,
  onClose
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewModalFile, setPreviewModalFile] = useState<FilePreviewItem | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const isImageFile = (fileName?: string, fileUrl?: string, msgType?: string) => {
    if (msgType === 'image') return true;
    const name = (fileName || fileUrl || '').toLowerCase();
    return /\.(png|jpg|jpeg|gif|webp|svg|bmp)$/i.test(name);
  };

  const isPdfFile = (fileName?: string, fileUrl?: string) => {
    const name = (fileName || fileUrl || '').toLowerCase();
    return /\.pdf$/i.test(name);
  };

  const handleOpenPreview = (fileUrl: string, fileName?: string) => {
    const isImg = isImageFile(fileName, fileUrl);
    const isPdf = isPdfFile(fileName, fileUrl);
    const type = isImg ? 'image' : isPdf ? 'pdf' : 'doc';
    const viewUrl = apiService.getFileViewUrl(fileUrl, fileName);

    setPreviewModalFile({
      url: viewUrl,
      name: fileName || (isImg ? 'Image' : 'Attachment'),
      type
    });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((newMessage.trim() || attachedFile) && !isUploading) {
      const mentions = extractMentions(newMessage);
      
      if (attachedFile) {
        try {
          setIsUploading(true);
          const uploadRes = await apiService.uploadChatFile(issue.id, attachedFile);
          onSendMessage(
            newMessage.trim(),
            mentions,
            uploadRes.file_url,
            uploadRes.file_name,
            uploadRes.file_size,
            uploadRes.message_type
          );
          setAttachedFile(null);
        } catch (error) {
          console.error('Failed to upload file to S3:', error);
          alert('Failed to upload file. Please try again.');
          return;
        } finally {
          setIsUploading(false);
        }
      } else {
        onSendMessage(newMessage.trim(), mentions);
      }
      
      setNewMessage('');
      setReplyTo(null);
    }
  };

  const handleEditMessage = (messageId: string) => {
    if (onEditMessage && editContent.trim()) {
      onEditMessage(messageId, editContent.trim());
      setEditingMessage(null);
      setEditContent('');
    }
  };

  const extractMentions = (content: string): string[] => {
    const mentionRegex = /@(\w+)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      const username = match[1].toLowerCase();
      const user = users.find(u => u.displayName.toLowerCase().includes(username));
      if (user) {
        mentions.push(user.id);
      }
    }
    
    return mentions;
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size <= maxSize) {
        setAttachedFile(file);
      } else {
        alert('File size must be less than 10MB');
      }
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getUserById = (userId: string) => {
    return users.find(user => user.id === userId);
  };

  const renderMessage = (message: ChatMessage) => {
    const user = message.user || getUserById(message.userId);
    const isCurrentUser = message.userId === currentUser.id;
    const isEditing = editingMessage === message.id;

    // Check if message came from Slack
    const isSlackMsg = message.content?.startsWith('[Slack');
    let displayAuthor = user?.displayName;
    let displayContent = message.content;

    if (isSlackMsg && message.content.startsWith('[Slack - ')) {
      const endBracket = message.content.indexOf(']');
      if (endBracket !== -1) {
        displayAuthor = message.content.substring(9, endBracket).trim();
        displayContent = message.content.substring(endBracket + 1).trim();
      }
    } else if (isSlackMsg && message.content.startsWith('[Slack] ')) {
      const colonIdx = message.content.indexOf(':');
      if (colonIdx !== -1) {
        displayAuthor = message.content.substring(8, colonIdx).trim();
        displayContent = message.content.substring(colonIdx + 1).trim();
      }
    }

    return (
      <div key={message.id} className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md ${isCurrentUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          <UserAvatar
            name={user?.displayName}
            src={user?.avatarUrl}
            className="w-8 h-8 border-2 border-white shadow-sm flex-shrink-0"
          />
          
          <div className={`rounded-2xl px-4 py-2 shadow-sm relative group ${
            isCurrentUser 
              ? 'bg-blue-600 text-white' 
              : 'bg-white text-gray-900 border border-gray-200'
          }`}>
            {/* Message Actions */}
            <div className={`absolute top-1 ${isCurrentUser ? 'left-1' : 'right-1'} opacity-0 group-hover:opacity-100 transition-opacity`}>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setReplyTo(message.id)}
                  className={`p-1 rounded hover:bg-black/10 ${isCurrentUser ? 'text-blue-100' : 'text-gray-400'}`}
                >
                  <Reply className="w-3 h-3" />
                </button>
                {isCurrentUser && (
                  <>
                    <button
                      onClick={() => {
                        setEditingMessage(message.id);
                        setEditContent(message.content);
                      }}
                      className={`p-1 rounded hover:bg-black/10 ${isCurrentUser ? 'text-blue-100' : 'text-gray-400'}`}
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => onDeleteMessage?.(message.id)}
                      className={`p-1 rounded hover:bg-black/10 ${isCurrentUser ? 'text-blue-100' : 'text-gray-400'}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Message Content */}
            {isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-2 py-1 text-sm bg-white text-gray-900 border border-gray-300 rounded"
                  onKeyPress={(e) => e.key === 'Enter' && handleEditMessage(message.id)}
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditMessage(message.id)}
                    className="text-xs bg-green-600 text-white px-2 py-1 rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingMessage(null)}
                    className="text-xs bg-gray-600 text-white px-2 py-1 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-sm">
                  {displayContent}
                  {message.edited && (
                    <span className={`text-xs ml-2 ${isCurrentUser ? 'text-blue-100' : 'text-gray-500'}`}>
                      (edited)
                    </span>
                  )}
                </div>
                
                {/* File Attachment */}
                {Boolean(message.fileUrl) && (
                  <div className={`mt-2 ${message.content ? 'pt-2 border-t border-black/10' : ''}`}>
                    {isImageFile(message.fileName, message.fileUrl, message.type) ? (
                      <div
                        onClick={() => handleOpenPreview(message.fileUrl!, message.fileName)}
                        className="group/img relative rounded-xl overflow-hidden cursor-pointer border border-black/10 shadow-sm max-w-[240px] bg-black/5 hover:opacity-95 transition-all"
                      >
                        <img
                          src={apiService.getFileViewUrl(message.fileUrl, message.fileName)}
                          alt={message.fileName || 'Attachment'}
                          className="w-full max-h-40 object-cover rounded-xl"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center space-x-1.5 text-white">
                          <span className="text-xs font-medium drop-shadow">Click to preview</span>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => handleOpenPreview(message.fileUrl!, message.fileName)}
                        className={`flex items-center space-x-2.5 p-2 rounded-xl cursor-pointer border transition-all ${
                          isCurrentUser
                            ? 'bg-white/10 hover:bg-white/20 border-white/20 text-white'
                            : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900'
                        }`}
                      >
                        <File className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs font-medium truncate flex-1" title={message.fileName}>
                          {message.fileName || 'Attachment'}
                        </span>
                        <a
                          href={apiService.getFileViewUrl(message.fileUrl, message.fileName, true)}
                          download={message.fileName}
                          onClick={e => e.stopPropagation()}
                          className="text-xs underline flex-shrink-0 ml-1"
                        >
                          Download
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Message Info */}
            <div className={`flex items-center justify-between mt-1 text-xs ${
              isCurrentUser ? 'text-blue-100' : 'text-gray-500'
            }`}>
              <span className="flex items-center space-x-1">
                <span>{displayAuthor || (user?.displayName ? user.displayName.split(' ')[0] : 'User')}</span>
                {isSlackMsg && (
                  <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-semibold bg-[#4A154B]/20 text-[#4A154B] dark:text-purple-300">
                    Slack
                  </span>
                )}
              </span>
              <span>{formatTime(message.timestamp)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 w-80 bg-white border border-gray-200 rounded-t-lg shadow-lg z-50">
        <div className="flex items-center justify-between p-3 bg-blue-600 text-white rounded-t-lg">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">{issue.key} Chat</span>
            <span className="text-xs bg-blue-500 px-2 py-0.5 rounded-full">
              {messages.length}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <button onClick={onToggleMinimize} className="p-1 hover:bg-blue-700 rounded">
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-white border border-gray-200 rounded-lg shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-blue-600 text-white rounded-t-lg">
        <div className="flex items-center space-x-2">
          <span className="font-medium">{issue.key} - Chat</span>
          <span className="text-xs bg-blue-500 px-2 py-0.5 rounded-full">
            {messages.length} messages
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button onClick={onToggleMinimize} className="p-1 hover:bg-blue-700 rounded">
            <Minimize2 className="w-4 h-4" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Participants */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-600">Participants:</span>
          <div className="flex -space-x-1">
            {issue.assignees.map(assigneeId => {
              const user = getUserById(assigneeId);
              return user ? (
                <UserAvatar
                  key={user.id}
                  name={user.displayName}
                  src={user.avatarUrl}
                  className="w-6 h-6 border-2 border-white"
                />
              ) : null;
            })}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-96">
        {messages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <UserIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start the conversation!</p>
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Indicator */}
      {replyTo && (
        <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-yellow-800">Replying to message</span>
            <button
              onClick={() => setReplyTo(null)}
              className="text-yellow-600 hover:text-yellow-800"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* File Attachment Preview */}
      {attachedFile && (
        <div className="px-4 py-2 bg-blue-50 border-t border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <File className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-900">{attachedFile.name}</span>
            </div>
            <button
              onClick={() => setAttachedFile(null)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileAttach}
            className="hidden"
          />
          
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <Paperclip className="w-4.5 h-4.5" />
          </button>
          
          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message... (use @name to mention)"
              className="w-full px-3 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          
          <button
            type="submit"
            disabled={!newMessage.trim() && !attachedFile}
            className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4.5 h-4.5" />
          </button>
        </div>
      </form>
      </div>

      <FilePreviewModal
        isOpen={Boolean(previewModalFile)}
        onClose={() => setPreviewModalFile(null)}
        file={previewModalFile}
      />
    </>
  );
};
